
import React, { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import logo from './assets/spoteq_logo.png';

interface NavbarProps {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ theme, toggleTheme }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const accentColor = "#00CCEB";

  const navStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0.5rem 1rem",
    background: theme === 'dark' ? "rgba(26, 26, 26, 0.85)" : "rgba(252, 255, 255, 0.85)",
    color: theme === 'dark' ? "white" : "black",
    borderBottom: theme === 'dark' ? "1px solid #333" : "1px solid #ccc",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    position: "sticky",
    top: 0,
    zIndex: 1000,
    width: "100%",
    boxSizing: "border-box",
    // Dark-theme cyan glow is animated via the `.navbar-glow` class below.
    boxShadow: theme === 'dark' ? undefined : "0 2px 8px rgba(0, 0, 0, 0.1)"
  };

  const menuContainerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "20px",
  };

  const getLinkStyle = ({ isActive }: { isActive: boolean }): React.CSSProperties => ({
    color: isActive ? accentColor : (theme === 'dark' ? "#e0e0e0" : "black"),
    textDecoration: "none",
    fontWeight: "bold",
  });

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <nav className={theme === 'dark' ? 'navbar-glow' : undefined} style={navStyle}>
      <Link to="/" style={{ display: 'flex', alignItems: 'center', zIndex: 1001 }}>
        <img 
          src={logo} 
          alt="SpotEQ Logo" 
          style={{ 
            height: '36px',
            width: 'auto',
            filter: theme === 'dark' ? 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.4))' : 'none'
          }} 
        />
      </Link>

      <div className="nav-content" style={{ display: 'flex', alignItems: 'center', gap: '20px', flex: 1, justifyContent: 'flex-end' }}>
        <div className={`nav-links ${isMenuOpen ? 'open' : ''}`} style={menuContainerStyle}>
          <NavLink to="/" style={getLinkStyle} end onClick={() => setIsMenuOpen(false)}>Home</NavLink>
          <NavLink to="/demo" style={getLinkStyle} onClick={() => setIsMenuOpen(false)}>Demo</NavLink>
          <NavLink to="/how-to-eq" style={getLinkStyle} onClick={() => setIsMenuOpen(false)}>How to EQ</NavLink>
          <NavLink to="/faq" style={getLinkStyle} onClick={() => setIsMenuOpen(false)}>FAQ</NavLink>
          <NavLink to="/features" style={getLinkStyle} onClick={() => setIsMenuOpen(false)}>Premium</NavLink>
          <NavLink to="/about" style={getLinkStyle} onClick={() => setIsMenuOpen(false)}>About</NavLink>
        </div>

        <button 
          onClick={toggleTheme}
          aria-label="Toggle theme"
          style={{
            padding: '8px',
            cursor: 'pointer',
            borderRadius: '50%',
            border: `1px solid ${accentColor}`,
            background: 'transparent',
            color: accentColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            zIndex: 1001
          }}
        >
          {theme === 'light' ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="5"></circle>
              <line x1="12" y1="1" x2="12" y2="3"></line>
              <line x1="12" y1="21" x2="12" y2="23"></line>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
              <line x1="1" y1="12" x2="3" y2="12"></line>
              <line x1="21" y1="12" x2="23" y2="12"></line>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
            </svg>
          )}
        </button>

        <div 
          onClick={toggleMenu}
          className="hamburger"
          style={{ 
            display: 'none', 
            cursor: 'pointer',
            flexDirection: 'column',
            gap: '5px',
            zIndex: 1001
          }}
        >
          <div style={{ width: '25px', height: '3px', background: accentColor, transition: '0.3s', transform: isMenuOpen ? 'rotate(45deg) translate(5px, 6px)' : 'none' }}></div>
          <div style={{ width: '25px', height: '3px', background: accentColor, opacity: isMenuOpen ? 0 : 1, transition: '0.3s' }}></div>
          <div style={{ width: '25px', height: '3px', background: accentColor, transition: '0.3s', transform: isMenuOpen ? 'rotate(-45deg) translate(5px, -6px)' : 'none' }}></div>
        </div>
      </div>

      <style>{`
        @keyframes navbarGlowPulse {
          0%, 100% { box-shadow: 0 4px 20px rgba(0, 204, 235, 0.15); }
          50%      { box-shadow: 0 4px 28px rgba(0, 204, 235, 0.30); }
        }
        .navbar-glow {
          animation: navbarGlowPulse 8s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .navbar-glow {
            animation: none;
            box-shadow: 0 4px 20px rgba(0, 204, 235, 0.15);
          }
        }
        @media (max-width: 960px) {
          .hamburger { display: flex !important; }
          .nav-links {
            display: none !important;
            position: absolute;
            top: 100%;
            left: 0;
            width: 100%;
            background: ${theme === 'dark' ? 'rgba(26, 26, 26, 0.95)' : 'rgba(252, 255, 255, 0.95)'};
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
            flex-direction: column !important;
            justify-content: flex-start;
            align-items: center;
            padding: 20px 0;
            gap: 20px !important;
            z-index: 1000;
            border-bottom: ${theme === 'dark' ? '1px solid #333' : '1px solid #ccc'};
            box-shadow: 0 4px 10px rgba(0,0,0,0.1);
          }
          .nav-links.open {
            display: flex !important;
          }
        }
      `}</style>
    </nav>
  );
};

export default Navbar;
